import React, { Component } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import {
  apiErrorMessage,
  firstError,
  hasErrors,
  validateBrandProduct,
} from "Helpers/ContentValidation";
import {
  brandProductFormToFormData,
  emptyBrandProductForm,
} from "Helpers/ContentPayloads";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { Card } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";

import BrandProductFormFields from "components/ContentForms/BrandProductFormFields";

export default class AddBrandProduct extends Component {
  state = {
    values: {
      ...emptyBrandProductForm(),
      // Pre-filled when arriving from a brand's "Manage products" link.
      brand: new URLSearchParams(window.location.search).get("brand") || "",
    },
    errors: {},
    brandOptions: [],
    // Left empty so the API appends the product to the end of its brand's list.
    sortOrderTouched: false,

    loading: false,
    notification: false,
    notiColor: "success",
    message: "",
    redirect: false,
  };

  componentDidMount() {
    this.fetchBrandOptions();
  }

  fetchBrandOptions = async () => {
    if (init() !== "success") return this.setState({ redirect: true });

    try {
      const response = await axios.get(Constants.getUrls.getBrandOptions);
      this.setState({ brandOptions: response.data?.data || [] });
    } catch (error) {
      this.notify("warning", apiErrorMessage(error, "Could not load brands"));
    }
  };

  handleChange = (name, value) => {
    this.setState((state) => {
      const errors = { ...state.errors };
      delete errors[name];
      return {
        values: { ...state.values, [name]: value },
        errors,
        sortOrderTouched:
          name === "sort_order" ? true : state.sortOrderTouched,
      };
    });
  };

  handleFile = (file) => {
    this.setState((state) => ({
      values: { ...state.values, image_file: file },
      errors: { ...state.errors, image: undefined },
    }));
  };

  notify = (notiColor, message) => {
    this.setState({ notification: true, notiColor, message });
    setTimeout(() => this.setState({ notification: false, message: "" }), 4000);
  };

  handleSubmit = async (event) => {
    event.preventDefault();

    const errors = validateBrandProduct(this.state.values, { isUpdate: false });
    if (hasErrors(errors)) {
      this.setState({ errors });
      return this.notify("warning", firstError(errors));
    }

    if (init() !== "success") return this.setState({ redirect: true });

    this.setState({ loading: true, errors: {} });

    const formData = brandProductFormToFormData(this.state.values);
    // Without an explicit order the API puts the product at the end of the
    // brand's list, which is what "add another product" should do.
    if (!this.state.sortOrderTouched) formData.delete("sort_order");

    try {
      const response = await axios.post(
        Constants.postUrls.createBrandProduct,
        formData
      );

      this.setState({ loading: false });
      this.notify("success", response.data?.message || "Product created successfully!");
      setTimeout(() => this.setState({ redirect: true }), 1200);
    } catch (error) {
      this.setState({ loading: false });
      this.notify("warning", apiErrorMessage(error, "Could not create the product"));
    }
  };

  render() {
    if (this.state.redirect) {
      const brand = this.state.values.brand;
      return (
        <Navigate to={brand ? `/brandproducts?brand=${brand}` : "/brandproducts"} replace />
      );
    }

    return (
      <DashboardLayout>
        <DashboardNavbar />

        <MDBox pt={6} pb={3}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                Add Brand Product
              </MDTypography>
            </MDBox>

            <MDBox py={3} px={2}>
              <form onSubmit={this.handleSubmit} noValidate>
                <BrandProductFormFields
                  values={this.state.values}
                  errors={this.state.errors}
                  onChange={this.handleChange}
                  onFile={this.handleFile}
                  brandOptions={this.state.brandOptions}
                />

                <MDBox mt={4} display="flex" gap={2}>
                  <MDButton
                    type="submit"
                    variant="gradient"
                    color="info"
                    disabled={this.state.loading}
                  >
                    {this.state.loading ? "Saving..." : "Create Product"}
                  </MDButton>

                  <MDButton
                    variant="outlined"
                    color="dark"
                    onClick={() => this.setState({ redirect: true })}
                    disabled={this.state.loading}
                  >
                    Cancel
                  </MDButton>
                </MDBox>
              </form>
            </MDBox>

            <MDSnackbar
              color={this.state.notiColor}
              icon="notifications"
              title="Brand Products"
              content={this.state.message}
              dateTime="Just now"
              open={this.state.notification}
              close={() => this.setState({ notification: false })}
            />
          </Card>
        </MDBox>
      </DashboardLayout>
    );
  }
}
